import { useId, useState, type FormEvent } from 'react';
import { Upload } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, ErroApi } from '../../../api/cliente';
import type {
  CertificadoA1,
  EmpresaCaptura,
  PlanoCaptura,
} from '../../../api/tipos';
import { Modal } from '../../../componentes/Modal';
import {
  Aviso,
  Botao,
  Campo,
  CaixaSelecao,
  Opcao,
  cx,
} from '../../../componentes/ui';
import { reais } from '../../../util/formato';
import {
  cnpjLegivel,
  dataCurta,
  diasParaVencer,
  faixaDoPlano,
  nomeDaFaixa,
} from './status';
import css from './AdicionarEmpresa.module.css';

const NOVO = 'novo';

/**
 * Cadastro de uma empresa na captura: escolhe (ou envia) o certificado A1,
 * confirma o CNPJ e o que o plano vai cobrar.
 *
 * O aceite do termo é obrigatório e explícito — o certificado é uma credencial
 * que age em nome da empresa na SEFAZ, e a LGPD exige que a pessoa saiba
 * exatamente para que vamos usá-lo.
 */
export function AdicionarEmpresa({
  aberto,
  aoFechar,
  aoConcluir,
  aoCertificadoNovo,
  certificados,
  custoCentavos,
  plano,
}: {
  aberto: boolean;
  aoFechar: () => void;
  aoConcluir: (empresa: EmpresaCaptura) => void;
  /** O certificado entrou mesmo que a empresa falhe depois (ex.: sem saldo). */
  aoCertificadoNovo: () => void;
  certificados: CertificadoA1[];
  /** O que ativar este CNPJ debita agora (`/dfe/plano`). */
  custoCentavos: number;
  plano: PlanoCaptura;
}) {
  const idForm = useId();
  const idArquivo = useId();
  const validos = certificados.filter((c) => diasParaVencer(c.validoAte) > 0);
  const [certificadoId, setCertificadoId] = useState<string>(
    validos[0]?.id ?? NOVO,
  );
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [senha, setSenha] = useState('');
  const [aceite, setAceite] = useState(false);
  const [cnpj, setCnpj] = useState('');
  const [nome, setNome] = useState('');
  const [manifestar, setManifestar] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const novo = certificadoId === NOVO;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    if (novo && (!arquivo || !senha)) {
      setErro('Escolha o arquivo do certificado e informe a senha.');
      return;
    }
    if (novo && !aceite) {
      setErro(
        'Para continuar, aceite o termo de autorização de uso do certificado.',
      );
      return;
    }

    setEnviando(true);
    try {
      let id = certificadoId;
      if (novo) {
        const form = new FormData();
        form.append('arquivo', arquivo!);
        form.append('senha', senha);
        form.append('aceiteTermo', 'true');
        const cert = await api<CertificadoA1>('/dfe/certificados', {
          metodo: 'POST',
          arquivo: form,
        });
        id = cert.id;
        // A senha não fica nem no estado da tela depois de usada.
        setSenha('');
        setArquivo(null);
        setCertificadoId(cert.id);
        aoCertificadoNovo();
      }
      const empresa = await api<EmpresaCaptura>('/dfe/empresas', {
        metodo: 'POST',
        corpo: {
          certificadoId: id,
          cnpj: cnpj.trim() || undefined,
          nome: nome.trim() || undefined,
          manifestarCiencia: manifestar,
        },
      });
      aoConcluir(empresa);
    } catch (err) {
      setErro(
        err instanceof ErroApi
          ? err.message
          : 'Não foi possível concluir agora.',
      );
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Adicionar empresa à captura"
      descricao="Com o certificado A1 da empresa, buscamos na SEFAZ todas as NF-e emitidas contra o CNPJ dela — sem precisar das chaves."
      largura={600}
      fechavel={!enviando}
      rodape={
        <>
          <Botao
            type="button"
            aparencia="secundario"
            onClick={aoFechar}
            disabled={enviando}
          >
            Cancelar
          </Botao>
          <Botao type="submit" form={idForm} carregando={enviando}>
            Ativar captura
          </Botao>
        </>
      }
    >
      <form id={idForm} onSubmit={(e) => void enviar(e)} className={css.form}>
        <fieldset className={css.grupo}>
          <legend className={css.legenda}>
            Certificado digital (e-CNPJ A1)
          </legend>
          {validos.map((c) => (
            <Opcao
              key={c.id}
              cartao
              name="certificado"
              checked={certificadoId === c.id}
              onChange={() => setCertificadoId(c.id)}
              descricao={`CNPJ ${cnpjLegivel(c.cnpj)} · válido até ${dataCurta(c.validoAte)}`}
            >
              {c.titular}
            </Opcao>
          ))}
          <Opcao
            cartao
            name="certificado"
            checked={novo}
            onChange={() => setCertificadoId(NOVO)}
            descricao="Arquivo .pfx ou .p12 com a senha."
          >
            Enviar um certificado novo
          </Opcao>
        </fieldset>

        {novo && (
          <div className={css.novo}>
            <div className={css.arquivo}>
              <label htmlFor={idArquivo} className={css.arquivoRotulo}>
                Arquivo do certificado
              </label>
              <input
                id={idArquivo}
                type="file"
                className={cx('sr-only', css.arquivoEntrada)}
                accept=".pfx,.p12,application/x-pkcs12"
                onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
              />
              <label htmlFor={idArquivo} className={css.arquivoEscolher}>
                <Upload aria-hidden="true" />
                <span className={css.arquivoNome}>
                  {arquivo ? arquivo.name : 'Escolher arquivo .pfx ou .p12'}
                </span>
              </label>
            </div>
            <Campo
              rotulo="Senha do certificado"
              type="password"
              autoComplete="off"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              dica="Usada só para abrir o certificado. Fica guardada criptografada junto com ele."
            />
            <CaixaSelecao
              checked={aceite}
              onChange={(e) => setAceite(e.target.checked)}
              className={css.termo}
            >
              Autorizo o BAIXA XML a guardar este certificado de forma
              criptografada e a usá-lo <strong>exclusivamente</strong> para
              consultar na SEFAZ as NF-e destinadas a esta empresa e registrar a
              Ciência da Operação delas. Declaro que tenho poderes para essa
              autorização e sei que posso revogá-la a qualquer momento removendo
              o certificado. Detalhes na{' '}
              <Link to="/privacidade#certificado-digital" target="_blank">
                Política de Privacidade
              </Link>
              .
            </CaixaSelecao>
          </div>
        )}

        <div className={css.duplo}>
          <Campo
            rotulo="CNPJ a monitorar"
            opcional
            placeholder="Vazio = o do certificado"
            value={cnpj}
            onChange={(e) => setCnpj(e.target.value)}
            dica="Para filial, informe o CNPJ dela (mesma raiz do certificado)."
          />
          <Campo
            rotulo="Nome para identificar"
            opcional
            placeholder="Vazio = o nome do certificado"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
          />
        </div>

        <CaixaSelecao
          checked={manifestar}
          onChange={(e) => setManifestar(e.target.checked)}
          descricao="Sem ela, a SEFAZ só entrega o resumo da nota, não o XML completo. A Ciência não confirma nem recusa a compra — só registra que a empresa viu a nota."
        >
          Enviar a Ciência da Operação automaticamente
        </CaixaSelecao>

        <Aviso tom="info">
          <CustoAtivacao plano={plano} custoCentavos={custoCentavos} />
        </Aviso>

        <Aviso tom="alerta">
          Se outro sistema (ERP, contador) também busca as notas deste CNPJ na
          SEFAZ, os dois disputam a mesma fila e a SEFAZ bloqueia as consultas
          por 1 hora. Mantenha só um sistema fazendo a captura.
        </Aviso>

        {erro && <Aviso>{erro}</Aviso>}
      </form>
    </Modal>
  );
}

/** Explica o débito de agora: plano novo, subida de faixa ou nada. */
function CustoAtivacao({
  plano,
  custoCentavos,
}: {
  plano: PlanoCaptura;
  custoCentavos: number;
}) {
  const cnpjs = plano.cnpjsAtivos + 1;
  const faixa = faixaDoPlano(cnpjs, plano.faixas, plano.adicionalCentavos);
  const nome = nomeDaFaixa(faixa.ate, cnpjs).replace(/^Até/, 'até');

  if (custoCentavos === 0) {
    return (
      <>
        Este CNPJ cabe na faixa que você já pagou:{' '}
        <strong>nada a cobrar agora</strong>. XMLs ilimitados.
      </>
    );
  }
  if (plano.ciclo) {
    return (
      <>
        Com este CNPJ a conta passa para o plano de {nome} (
        {reais(faixa.centavos)}/mês). Agora debitamos só a diferença
        proporcional até {dataCurta(plano.ciclo.fim)}:{' '}
        <strong>{reais(custoCentavos)}</strong>. XMLs ilimitados.
      </>
    );
  }
  return (
    <>
      Plano de {nome}: <strong>{reais(custoCentavos)}</strong> por mês, debitado
      agora dos seus créditos. XMLs ilimitados, sem fidelidade.
    </>
  );
}
